import React from "react";
import { useTranslation } from "react-i18next";
import type { DepositRequired } from "../../../common/jsonRpcTypes";
import type { PreorderSelections } from "./BookingForm";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import BookingTypeGroupSummary from "../shared/BookingTypeGroupSummary";

interface PaymentSummaryProps {
  readonly isDepositOnly: boolean;
  readonly isAuthOnly: boolean;
  readonly depositRequired: DepositRequired | null | undefined;
  readonly isDepositTaxExclusive: boolean;
  readonly preorderSelections: PreorderSelections | null;
  readonly isPreorderTaxExclusive: boolean;
  readonly paymentAmount: number;
  readonly isEffectiveTaxExclusive: boolean;
  readonly effectiveTaxLabel: string;
  readonly showVoucherInput: boolean;
  readonly redeemedTotal: number;
  readonly amountToPayOnCard: number;
  readonly currency: string;
  readonly countryCode: string;
  readonly preorderTotal?: number;
  readonly preorderRequiredPayment?: number;
}

/**
 * Helper component for rendering individual payment line items
 */
const PaymentLineItem: React.FC<{
  label: string;
  amount: number;
  currency: string;
  countryCode: string;
  isHighlighted?: boolean;
}> = ({ label, amount, currency, countryCode, isHighlighted = false }) => (
  <div
    className={`gcw:flex gcw:justify-between gcw:text-body gcw:text-text-secondary gcw:mb-1 ${isHighlighted ? "gcw:font-semibold" : ""}`}
  >
    <span>{label}</span>
    <span>{formatCurrency(amount, currency, countryCode)}</span>
  </div>
);

/**
 * Helper component for rendering deposit section
 */
const DepositSection: React.FC<{
  depositRequired: DepositRequired | null | undefined;
  isAuthOnly: boolean;
  isDepositTaxExclusive: boolean;
  currency: string;
  countryCode: string;
  t: any;
  takeWithPreorder: boolean;
  hasPreorder: boolean;
}> = ({
  depositRequired,
  isAuthOnly,
  isDepositTaxExclusive,
  currency,
  countryCode,
  t,
  takeWithPreorder,
  hasPreorder,
}) => {
  if (!depositRequired || isAuthOnly) {
    return null;
  }

  // When takeWithPreorder is false and preorder exists, don't show deposit section
  if (!takeWithPreorder && hasPreorder) {
    return null;
  }

  return (
    <>
      <PaymentLineItem
        label={isDepositTaxExclusive ? t("net_label", "Net") : t("booking_deposit_amount")}
        amount={depositRequired.total}
        currency={currency}
        countryCode={countryCode}
      />
      {isDepositTaxExclusive &&
        depositRequired.formatted_tax_rates!.map((rate) => (
          <PaymentLineItem
            key={rate.label}
            label={rate.label}
            amount={rate.tax}
            currency={currency}
            countryCode={countryCode}
          />
        ))}
      {isDepositTaxExclusive && depositRequired.gross_amount != null && (
        <PaymentLineItem
          label={t("booking_deposit_amount")}
          amount={depositRequired.gross_amount}
          currency={currency}
          countryCode={countryCode}
          isHighlighted={true}
        />
      )}
    </>
  );
};

/**
 * Helper component for rendering total order amount when payment percentage is less than 100%
 */
const TotalOrderAmount: React.FC<{
  total: number;
  currency: string;
  countryCode: string;
  t: any;
}> = ({ total, currency, countryCode, t }) => (
  <PaymentLineItem
    label={t("booking_preorder_total", "Total order amount")}
    amount={total}
    currency={currency}
    countryCode={countryCode}
    isHighlighted={true}
  />
);

/**
 * Helper component for rendering tax breakdown section
 */
const TaxBreakdownSection: React.FC<{
  isDepositOnly: boolean;
  preorderSelections: PreorderSelections | null;
  isPreorderTaxExclusive: boolean;
  currency: string;
  countryCode: string;
  t: any;
}> = ({ isDepositOnly, preorderSelections, isPreorderTaxExclusive, currency, countryCode, t }) => {
  if (isDepositOnly || !isPreorderTaxExclusive || !preorderSelections || preorderSelections.total <= 0) {
    return null;
  }

  return (
    <>
      {preorderSelections.taxBreakdown?.map((entry) => (
        <PaymentLineItem
          key={entry.label}
          label={entry.label}
          amount={entry.amount}
          currency={currency}
          countryCode={countryCode}
        />
      ))}
    </>
  );
};

/**
 * Helper component for rendering gross total section
 */
const GrossTotalSection: React.FC<{
  isDepositOnly: boolean;
  preorderSelections: PreorderSelections | null;
  isPreorderTaxExclusive: boolean;
  currency: string;
  countryCode: string;
  t: any;
  getPreorderPaymentAmount: () => number;
}> = ({
  isDepositOnly,
  preorderSelections,
  isPreorderTaxExclusive,
  currency,
  countryCode,
  t,
  getPreorderPaymentAmount,
}) => {
  if (
    isDepositOnly ||
    !isPreorderTaxExclusive ||
    !preorderSelections ||
    preorderSelections.total <= 0 ||
    !preorderSelections.totalGross
  ) {
    return null;
  }

  return (
    <PaymentLineItem
      label={t("booking_preorder_payment")}
      amount={getPreorderPaymentAmount()}
      currency={currency}
      countryCode={countryCode}
      isHighlighted={true}
    />
  );
};

/**
 * Helper component for rendering voucher section
 */
const VoucherSection: React.FC<{
  showVoucherInput: boolean;
  redeemedTotal: number;
  amountToPayOnCard: number;
  currency: string;
  countryCode: string;
  t: any;
}> = ({ showVoucherInput, redeemedTotal, amountToPayOnCard, currency, countryCode, t }) => {
  if (!showVoucherInput || redeemedTotal <= 0) {
    return null;
  }

  return (
    <>
      <div className="gcw:flex gcw:justify-between gcw:text-body gcw:text-text-secondary gcw:mt-1">
        <span>{t("booking_voucher_redeemed", "Voucher redeemed")}</span>
        <span>-{formatCurrency(redeemedTotal, currency, countryCode)}</span>
      </div>
      <div className="gcw:flex gcw:justify-between gcw:text-body gcw:font-bold gcw:text-text-primary gcw:mt-2 gcw:pt-2 gcw:border-t gcw:border-gray-200">
        <span>{t("booking_voucher_amount_on_card", "Amount to pay on card")}</span>
        <span>{formatCurrency(amountToPayOnCard, currency, countryCode)}</span>
      </div>
    </>
  );
};

/**
 * Helper component for rendering preorder section with service charge
 */
const PreorderWithServiceCharge: React.FC<{
  preorderSelections: PreorderSelections;
  isPreorderTaxExclusive: boolean;
  preorderRequiredPayment: number | undefined;
  currency: string;
  countryCode: string;
  t: any;
}> = ({ preorderSelections, isPreorderTaxExclusive, preorderRequiredPayment, currency, countryCode, t }) => (
  <>
    <PaymentLineItem
      label={isPreorderTaxExclusive ? t("net_label", "Net") : t("subtotal", "Subtotal")}
      amount={preorderSelections.subtotal}
      currency={currency}
      countryCode={countryCode}
    />
    <PaymentLineItem
      label={`${t("discretionary_service_charge", "Discretionary service charge")}${
        preorderSelections.serviceChargePercentage > 0 ? ` (${preorderSelections.serviceChargePercentage}%)` : ""
      }`}
      amount={preorderSelections.serviceChargeAmount}
      currency={currency}
      countryCode={countryCode}
    />
    {/* Show total order amount when payment percentage is less than 100% */}
    {preorderRequiredPayment !== undefined && preorderRequiredPayment < preorderSelections.total && (
      <TotalOrderAmount total={preorderSelections.total} currency={currency} countryCode={countryCode} t={t} />
    )}
    {/* Show required payment amount when payment percentage is less than 100% */}
    {preorderRequiredPayment !== undefined && preorderRequiredPayment < preorderSelections.total && (
      <AmountToPayNow
        requiredPayment={preorderRequiredPayment}
        requiredPaymentGross={preorderSelections.requiredPaymentGross}
        isPreorderTaxExclusive={isPreorderTaxExclusive}
        currency={currency}
        countryCode={countryCode}
        t={t}
      />
    )}
  </>
);

/**
 * Helper component for rendering preorder section without service charge
 */
const PreorderWithoutServiceCharge: React.FC<{
  preorderSelections: PreorderSelections;
  isPreorderTaxExclusive: boolean;
  preorderRequiredPayment: number | undefined;
  currency: string;
  countryCode: string;
  t: any;
}> = ({ preorderSelections, isPreorderTaxExclusive, preorderRequiredPayment, currency, countryCode, t }) => {
  const isPartialPayment = preorderRequiredPayment !== undefined && preorderRequiredPayment < preorderSelections.total;

  if (isPartialPayment) {
    return (
      <>
        <TotalOrderAmount total={preorderSelections.total} currency={currency} countryCode={countryCode} t={t} />
        <AmountToPayNow
          requiredPayment={preorderRequiredPayment}
          requiredPaymentGross={preorderSelections.requiredPaymentGross}
          isPreorderTaxExclusive={isPreorderTaxExclusive}
          currency={currency}
          countryCode={countryCode}
          t={t}
        />
      </>
    );
  }

  return (
    <PaymentLineItem
      label={isPreorderTaxExclusive ? t("net_label", "Net") : t("booking_preorder_payment")}
      amount={preorderSelections.total}
      currency={currency}
      countryCode={countryCode}
    />
  );
};

/**
 * Helper component for rendering preorder section
 */
const PreorderSection: React.FC<{
  isDepositOnly: boolean;
  preorderSelections: PreorderSelections | null;
  isPreorderTaxExclusive: boolean;
  preorderRequiredPayment: number | undefined;
  currency: string;
  countryCode: string;
  t: any;
  takeWithPreorder: boolean;
  hasDeposit: boolean;
}> = ({
  isDepositOnly,
  preorderSelections,
  isPreorderTaxExclusive,
  preorderRequiredPayment,
  currency,
  countryCode,
  t,
  takeWithPreorder,
  hasDeposit,
}) => {
  if (isDepositOnly || !preorderSelections || preorderSelections.total <= 0) {
    return null;
  }

  // When takeWithPreorder is false and deposit exists, we still show preorder
  // because when takeWithPreorder is false and preorder exists, we pay preorder only
  // (this is handled in PaymentStep calculation)

  if (preorderSelections.serviceChargeAmount > 0) {
    return (
      <PreorderWithServiceCharge
        preorderSelections={preorderSelections}
        isPreorderTaxExclusive={isPreorderTaxExclusive}
        preorderRequiredPayment={preorderRequiredPayment}
        currency={currency}
        countryCode={countryCode}
        t={t}
      />
    );
  }

  return (
    <PreorderWithoutServiceCharge
      preorderSelections={preorderSelections}
      isPreorderTaxExclusive={isPreorderTaxExclusive}
      preorderRequiredPayment={preorderRequiredPayment}
      currency={currency}
      countryCode={countryCode}
      t={t}
    />
  );
};

/**
 * Helper component for rendering amount to pay now when payment percentage is less than 100%
 */
const AmountToPayNow: React.FC<{
  requiredPayment: number;
  requiredPaymentGross: number | null | undefined;
  isPreorderTaxExclusive: boolean;
  currency: string;
  countryCode: string;
  t: any;
}> = ({ requiredPayment, requiredPaymentGross, isPreorderTaxExclusive, currency, countryCode, t }) => (
  <PaymentLineItem
    label={t("booking_preorder_payment_percentage", "Amount to pay now")}
    amount={isPreorderTaxExclusive && requiredPaymentGross != null ? requiredPaymentGross : requiredPayment}
    currency={currency}
    countryCode={countryCode}
  />
);

/**
 * PaymentSummary — the breakdown panel shown on the booking payment step.
 *
 * Presentational only: it renders deposit, pre-order, tax and voucher lines
 * from values already derived by PaymentStep. Extracted to keep PaymentStep's
 * cognitive complexity within the SonarQube threshold (S3776).
 */
const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  isDepositOnly,
  isAuthOnly,
  depositRequired,
  isDepositTaxExclusive,
  preorderSelections,
  isPreorderTaxExclusive,
  paymentAmount,
  isEffectiveTaxExclusive,
  effectiveTaxLabel,
  showVoucherInput,
  redeemedTotal,
  amountToPayOnCard,
  currency,
  countryCode,
  preorderTotal,
  preorderRequiredPayment,
}) => {
  const { t } = useTranslation();

  const takeWithPreorder = depositRequired?.takeWithPreorder ?? false;
  // Only treat the deposit as "paid alongside the pre-order" when the pre-order
  // is actually payable now — a pay-at-venue pre-order (requiredPaymentAmount 0)
  // must not hide the deposit line, since the deposit is the only amount due.
  const hasPreorder = !!(preorderSelections && preorderSelections.requiredPaymentAmount > 0);
  const hasDeposit = !!depositRequired && !isAuthOnly;

  const getPreorderPaymentAmount = (): number => {
    // For partial payments, show the partial payment gross amount instead of full gross amount
    if (
      preorderRequiredPayment !== undefined &&
      preorderSelections &&
      preorderRequiredPayment < preorderSelections.total
    ) {
      if (isPreorderTaxExclusive && preorderSelections.requiredPaymentGross != null) {
        return preorderSelections.requiredPaymentGross;
      }
      return preorderRequiredPayment;
    }
    return preorderSelections?.totalGross ?? 0;
  };

  return (
    <div
      id="gc-payment-summary"
      className="gcw:rounded-md gcw:border gcw:border-gray-200 gcw:bg-bg-surface gcw:p-bs-3 gcw:mb-bs-3"
    >
      <h3 className="gcw:text-lg gcw:font-heading gcw:font-bold gcw:text-text-primary gcw:mb-2">
        {t("booking_payment_summary")}
      </h3>

      {!isDepositOnly && (preorderSelections?.bookingTypeGroups?.length ?? 0) > 0 && (
        <BookingTypeGroupSummary
          groups={preorderSelections?.bookingTypeGroups ?? []}
          currency={currency}
          countryCode={countryCode}
          headingLevel="h4"
          className="gcw:mb-bs-3 gcw:pb-2 gcw:border-b gcw:border-gray-200"
        />
      )}

      <DepositSection
        depositRequired={depositRequired}
        isAuthOnly={isAuthOnly}
        isDepositTaxExclusive={isDepositTaxExclusive}
        currency={currency}
        countryCode={countryCode}
        t={t}
        takeWithPreorder={takeWithPreorder}
        hasPreorder={hasPreorder}
      />

      <PreorderSection
        isDepositOnly={isDepositOnly}
        preorderSelections={preorderSelections}
        isPreorderTaxExclusive={isPreorderTaxExclusive}
        preorderRequiredPayment={preorderRequiredPayment}
        currency={currency}
        countryCode={countryCode}
        t={t}
        takeWithPreorder={takeWithPreorder}
        hasDeposit={hasDeposit}
      />

      <TaxBreakdownSection
        isDepositOnly={isDepositOnly}
        preorderSelections={preorderSelections}
        isPreorderTaxExclusive={isPreorderTaxExclusive}
        currency={currency}
        countryCode={countryCode}
        t={t}
      />

      <GrossTotalSection
        isDepositOnly={isDepositOnly}
        preorderSelections={preorderSelections}
        isPreorderTaxExclusive={isPreorderTaxExclusive}
        currency={currency}
        countryCode={countryCode}
        t={t}
        getPreorderPaymentAmount={getPreorderPaymentAmount}
      />

      <div className="gcw:flex gcw:justify-between gcw:text-body gcw:font-bold gcw:text-text-primary gcw:mt-2 gcw:pt-2 gcw:border-t gcw:border-gray-200">
        <span>
          {t("amount_to_pay")}
          {!isEffectiveTaxExclusive && ` (${t("inc_vat", "inc {{taxLabel}}", { taxLabel: effectiveTaxLabel })})`}
        </span>
        <span>{formatCurrency(paymentAmount, currency, countryCode)}</span>
      </div>

      <VoucherSection
        showVoucherInput={showVoucherInput}
        redeemedTotal={redeemedTotal}
        amountToPayOnCard={amountToPayOnCard}
        currency={currency}
        countryCode={countryCode}
        t={t}
      />
    </div>
  );
};

export default PaymentSummary;
